import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ArticleBannerResponseItem, contentsDetailApi } from '../apis';
import {
  ContentsDetailQueryString,
  GetContentsDetailDataReturn,
} from '../types';

import { clientAxios } from '@/shared/api';
import { ApiResponse } from '@/shared/types';

interface ArticleBannerQueryParams {
  newsId: number;
}

export const useArticleBannerQuery = ({ newsId }: ArticleBannerQueryParams) => {
  return useQuery({
    queryKey: ['articleBannerApi', newsId],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleBannerResponseItem[]>
      >('/articleBannerApi/', { params: { newsId } });

      const { text, sub_text, sub_image, background_color, target_url } =
        data.data[0];

      const isProd = process.env.NEXT_PUBLIC_APP_STAGE === 'prod';
      const link = isProd
        ? `${target_url}?utm_source=yozmit&utm_medium=banner&utm_campaign=sticky&utm_content=${newsId}`
        : target_url;

      return {
        title: text,
        linkText: sub_text,
        link,
        backgroundColor: background_color,
        image: sub_image,
      };
    },
  });
};

export const getContentsDetailData = async ({
  id,
  queryString,
}: {
  id: string;
  queryString?: ContentsDetailQueryString;
}): Promise<GetContentsDetailDataReturn | null> => {
  try {
    const data = await contentsDetailApi({ id, queryString });

    const {
      id: contentsId,
      title,
      author,
      description,
      date_published,
      date_modified,
      repr_date_published,
      read_time,
      is_popular_news,
      category,
      content,
      view_count,
      thumbnail_image,
    } = data.data;

    return {
      id: contentsId,
      title,
      author: {
        authorId: author.id,
        name: author.name,
        urlIdentifier: author.author_url_identifier,
        imgUrl: author.image_url,
      },
      thumbnail: thumbnail_image,
      description,
      datePublished: date_published,
      dateModified: date_modified,
      date: repr_date_published,
      time: read_time,
      isPopular: is_popular_news,
      category: category[0],
      content,
      viewCount: view_count,
    };
  } catch (e) {
    console.error(
      JSON.stringify({
        'error-log': {
          message: 'getContentsDetailData error',
          id,
          error: e,
        },
      })
    );

    if (e instanceof AxiosError) {
      throw e;
    }

    throw new Error();
  }
};
